import React, { useContext } from 'react';

import Axios from 'axios';
import { queryClient } from 'QueryClientProvider';

import ls from 'local-storage';

import { readUserLogs, setLsLogs } from '@libs/NwLogs';

import { ListContext } from '@contexts/ListContext';

//Set the default Status at application start
const USER_DEFAULT_STATE = {
    currentUser: null,
};

const ART_STORAGE_KEY_PREFIXES = ['nw_config_art_news', 'nw_config_art_articles', 'nw_config_art_galleries'];
const ART_STORAGE_KEYS = [
    'nw_config_art_news',
    'nw_config_art_news_sections',
    'nw_config_art_news_tags',
    'nw_config_art_news_filters',
    'nw_config_art_articles',
    'nw_config_art_articles_filters',
    'nw_config_art_galleries',
    'nw_config_art_galleries_filters',
];

const getArtLocalStorageKeys = () => {
    const storageKeys = new Set(ART_STORAGE_KEYS);

    if (typeof localStorage !== 'undefined') {
        for (let index = 0; index < localStorage.length; index += 1) {
            const key = localStorage.key(index);
            if (
                key &&
                ART_STORAGE_KEY_PREFIXES.some(
                    (storageKeyPrefix) => key === storageKeyPrefix || key.startsWith(`${storageKeyPrefix}_`)
                )
            ) {
                storageKeys.add(key);
            }
        }
    }

    return [...storageKeys];
};

const readArtStorageSnapshot = () => {
    const artStorageSnapshot = {};

    getArtLocalStorageKeys().forEach((storageKey) => {
        const storageValue = ls.get(storageKey);
        if (storageValue !== null && storageValue !== undefined) {
            artStorageSnapshot[storageKey] = storageValue;
        }
    });

    return artStorageSnapshot;
};

const restoreArtStorageSnapshot = (artStorageSnapshot) => {
    Object.keys(artStorageSnapshot).forEach((storageKey) => {
        ls.set(storageKey, artStorageSnapshot[storageKey]);
    });
};

const clearLocalStorageKeepingArtData = () => {
    const artStorageSnapshot = readArtStorageSnapshot();

    ls.clear();

    restoreArtStorageSnapshot(artStorageSnapshot);
};

const UserContext = React.createContext(USER_DEFAULT_STATE);

const normalizePolicyLevel = (policyObject) => {
    const policyLevel = policyObject?.LevelName ?? policyObject?.Level;
    const normalizedPolicyLevel = typeof policyLevel === 'string' ? policyLevel.toLowerCase() : policyLevel;

    if (normalizedPolicyLevel === 0 || normalizedPolicyLevel === '0' || normalizedPolicyLevel === 'noaccess') {
        return 'NoAccess';
    }
    if (normalizedPolicyLevel === 1 || normalizedPolicyLevel === '1' || normalizedPolicyLevel === 'personal') {
        return 'Personal';
    }
    if (normalizedPolicyLevel === 2 || normalizedPolicyLevel === '2' || normalizedPolicyLevel === 'all') {
        return 'All';
    }

    return policyLevel;
};

const useUserPermission = (policyName, userIds, isMine) => {
    const { currentUser } = useContext(UserContext);
    const userPolicies = currentUser?.UserProfile?.Policies;

    //const shouldDebugPolicy = ['DataUpdateRequest', 'GrantExternalAccess'].includes(policyName);
    const shouldDebugPolicy = false;

    const debugPermissionResult = (result) => {
        if (shouldDebugPolicy) {
            console.log(`[useUserPermission] ${policyName}:`, result);
        }
        return result;
    };
    if (!policyName || !Array.isArray(userPolicies)) {
        return debugPermissionResult(false);
    }
    const policyObject = userPolicies.find((policy) => policy.Name?.toLowerCase() === policyName.toLowerCase());
    if (policyObject) {
        const policyLevel = normalizePolicyLevel(policyObject);
        if (policyLevel === 'All') {
            return debugPermissionResult(true);
        }
        if (policyLevel === 'Personal') {
            if (isMine) {
                return debugPermissionResult(true);
            }
            if (userIds && userIds.length) {
                if (userIds.find((userId) => String(userId) === String(currentUser?.UserID))) {
                    return debugPermissionResult(true);
                }
            }
            return debugPermissionResult(false);
        }
        return debugPermissionResult(false);
    }
    return debugPermissionResult(false);
};

//Create an exportable consumer that can be injected into components
const UserContextConsumer = UserContext.Consumer;

// Create the provider using a traditional React.Component class
class UserContextProvider extends React.Component {
    constructor(props) {
        super(props);

        const currentUser = JSON.parse(sessionStorage.getItem('currentUser'));
        this.state = {
            ...USER_DEFAULT_STATE,
            currentUser,
            //Metodi
            Login: this.login,
            Logout: this.logout,
            ForceClientLogout: this.forceclientlogout,
            UpdateCurrentUser: this.updateCurrentUser,
            UpdateUserCover: this.updateUserCover,
            RemoveLocalStorage: this.removelocalstorage,
        };
    }

    updateUserCover = () => {
        const { cachedList } = this.context;
        const user = cachedList.users.items.find((usr) => usr.ID === this.state.currentUser.UserID);
        const newCurrentUserInfo = {
            ...this.state.currentUser,
            CoverGuid: user.CoverGuid,
        };
        this.setState({ currentUser: newCurrentUserInfo });
        sessionStorage.setItem('currentUser', JSON.stringify(newCurrentUserInfo));
    };

    updateCurrentUser = (userInfo) => {
        this.setState({ currentUser: userInfo });
        sessionStorage.setItem('currentUser', JSON.stringify(userInfo));
    };

    login = async (currentUser, from) => {
        //Metto il loading a true e carico il currentUser
        this.setState(
            () => ({ currentUser }),
            () => {}
        );

        try {
            const tenantConfigurations = await Axios.get('/tenantconfigurations');
            ls.set('nw_tenant_configurations', tenantConfigurations);
        } catch (error) {
            console.error('Get Tenant Configurations Error => ', error);
        }
    };

    logout = () => {
        Axios.post(`/account/logoff/${this.state.currentUser.UserID}`)
            .then((response) => {
                sessionStorage.clear();
                clearLocalStorageKeepingArtData();
                queryClient.clear();
                this.setState(
                    () => ({ currentUser: null }),
                    () => {
                        console.error('logout', this.state);
                    }
                );
            })
            .catch((error) => {
                console.error('error on logoff', error);
            });
    };

    removelocalstorage = () => {
        var logsInStorage = readUserLogs();
        clearLocalStorageKeepingArtData();
        if (logsInStorage && logsInStorage.length > 0) {
            setLsLogs(logsInStorage);
        }
    };

    forceclientlogout = () => {
        const { cachedList } = this.context;
        sessionStorage.removeItem('currentUser');
        sessionStorage.removeItem('model_tabs_state');
        sessionStorage.removeItem('customer_tabs_state');
        sessionStorage.removeItem('model_agencies');
        sessionStorage.removeItem('model_statements');
        sessionStorage.removeItem('customer_statements');
        sessionStorage.removeItem('model_job_stats');
        ls.remove('nw_cachedlist_cities');
        ls.remove('selectedEvent');
        Object.keys(cachedList).map((key) => {
            ls.remove(`nw_cachedlist_${key}`);
            ls.remove(`nw_cachedlist_${key}_lastupdate`);
            return null;
        });
        ls.remove('nw_config_prefetch');
        queryClient.clear();
        this.setState(
            () => ({ currentUser: null }),
            () => {
                console.error('logout', this.state);
            }
        );
    };

    render() {
        return <UserContext.Provider value={this.state}>{this.props.children}</UserContext.Provider>;
    }
}

UserContextProvider.contextType = ListContext;
export { UserContext, UserContextProvider, UserContextConsumer, useUserPermission };
